import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { toast } from 'react-toastify';

import Modal from '../../../../components/Modal/Modal';
import { updateDunning } from '../../../../services/finance/dunningApi';
import {
   ControlSelect,
   FieldHint,
   FieldLabel,
   PrimaryActionButton,
   SecondaryButton,
} from '../../../../styles/dunning/base';
import { financeErrorMessage } from '../../utils/apiError';

// ---------------------------------------------------------------------------
// "Atribuir responsável" — reatribuição independente do compromisso (decisão
// 2026-08-17): PATCH /:id SÓ com assigned_user_id, sem tocar next_action_at.
// Só existe pra quem tem finance:dunning:read_all (restrito não reatribui —
// Feature 1) E finance:dunning:update (gestão, 0100); o gatilho é o responsável
// no cabeçalho do drawer.
//
// A lista vem de GET /billing-dunnings/assignees (0100 §3.2) — só quem tem
// `finance:dunning:assignable`. Quem não tem a chave não aparece aqui mesmo
// sendo usuário ativo: é a causa nº 1 de "criei o usuário e não consigo
// atribuir o caso a ele", por isso o hint abaixo diz isso em voz alta.
// ---------------------------------------------------------------------------

export default function AssignModal({
   isOpen,
   caseData = null,
   assignees,
   onClose,
   onSaved,
}) {
   const [assignee, setAssignee] = useState('');
   const [isSaving, setIsSaving] = useState(false);

   // Rascunho re-semeado a cada abertura com o estado atual do caso.
   useEffect(() => {
      if (!isOpen) return;
      setAssignee(
         caseData?.assigned_user ? String(caseData.assigned_user.id) : ''
      );
   }, [isOpen, caseData]);

   const handleSave = async () => {
      if (!caseData) return;
      setIsSaving(true);
      try {
         const updated = await updateDunning(caseData.id, {
            assignedUserId: assignee ? Number(assignee) : null,
         });
         toast.success(
            assignee
               ? 'Responsável atualizado.'
               : 'Caso ficou sem responsável.'
         );
         onSaved(updated);
         onClose();
      } catch (err) {
         toast.error(
            financeErrorMessage(err, 'Não foi possível atribuir o responsável.')
         );
      } finally {
         setIsSaving(false);
      }
   };

   return (
      <Modal
         isOpen={isOpen}
         title="Atribuir responsável"
         onClose={onClose}
         maxWidth="460px"
         footer={
            <>
               <SecondaryButton type="button" onClick={onClose}>
                  Cancelar
               </SecondaryButton>
               <PrimaryActionButton
                  type="button"
                  onClick={handleSave}
                  disabled={isSaving}
               >
                  {isSaving ? 'Salvando...' : 'Salvar'}
               </PrimaryActionButton>
            </>
         }
      >
         <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            <div>
               <FieldLabel htmlFor="dunning-assign-user">
                  Responsável
               </FieldLabel>
               <ControlSelect
                  id="dunning-assign-user"
                  value={assignee}
                  onChange={(event) => setAssignee(event.target.value)}
               >
                  <option value="">Sem responsável</option>
                  {assignees.map((user) => (
                     <option key={user.id} value={String(user.id)}>
                        {user.nome}
                     </option>
                  ))}
               </ControlSelect>
            </div>
            <FieldHint style={{ marginTop: 0 }}>
               Não altera o compromisso do caso. A lista traz só quem pode
               responder por casos de cobrança — se faltar alguém, o perfil de
               acesso dele precisa da permissão de responsável pela régua.
            </FieldHint>
         </div>
      </Modal>
   );
}

AssignModal.propTypes = {
   isOpen: PropTypes.bool.isRequired,
   // eslint-disable-next-line react/forbid-prop-types
   caseData: PropTypes.object,
   // eslint-disable-next-line react/forbid-prop-types
   assignees: PropTypes.array.isRequired,
   onClose: PropTypes.func.isRequired,
   onSaved: PropTypes.func.isRequired,
};
